import { useEffect } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { BenefitReportStatus } from '~/graphql/schema';

import { isReportComplete, isReportPending } from './Notifications';

import { GeneratedReportsDocument } from '../generated-reports-table/graphql/query.generatedReports.generated';

// Custom hook to store and manage notifications with Jotai atom
// Notifications are stored in session storage
export type Notification = {
  reportId: string;
  reportName: string;
  reportFilename?: string;
  status: BenefitReportStatus;
  dismissed: boolean;
};

export const notificationsAtom = atomWithStorage<Notification[]>(
  'report-notifications',
  [],
  createJSONStorage(() => sessionStorage),
  {
    getOnInit: true,
  }
);

type UseNotificationsHookResult = {
  addNotification: (reportId: string, status: BenefitReportStatus, reportName: string) => void;
  dequeueNotification: (reportId: string) => void;
  updateNotificationStatus: (reportId: string, status: BenefitReportStatus, reportFilename: string) => void;
  dismissNotification: (reportId: string) => void;
  notifications: Notification[];
};

export function useNotifications(): UseNotificationsHookResult {
  const [notifications, setNotifications] = useAtom(notificationsAtom);

  function addNotification(reportId: string, status: BenefitReportStatus, reportName: string) {
    const notification = { reportId, status, reportName, dismissed: false };
    setNotifications(currentNotifications => [...currentNotifications, notification]);
  }

  function dequeueNotification(reportId: string) {
    setNotifications(currentNotifications => {
      return currentNotifications.filter(notification => notification.reportId !== reportId);
    });
  }

  function updateNotificationStatus(reportId: string, status: BenefitReportStatus, reportFilename: string) {
    setNotifications(currentNotifications => {
      return currentNotifications.map(notification =>
        notification.reportId === reportId ? { ...notification, status, reportFilename } : notification
      );
    });
  }

  function dismissNotification(reportId: string) {
    setNotifications(currentNotifications => {
      return currentNotifications.map(notification =>
        notification.reportId === reportId ? { ...notification, dismissed: true } : notification
      );
    });
  }

  return { dequeueNotification, addNotification, updateNotificationStatus, dismissNotification, notifications };
}

// Custom hook to poll for report status, updating the notifications as needed
export const useReportStatusPolling = (
  notifications: Notification[],
  updateNotificationStatus: (reportId: string, status: BenefitReportStatus, reportFilename: string) => void,
  employerId: string
) => {
  const { data, startPolling, stopPolling } = useGraphQLQuery(GeneratedReportsDocument, {
    variables: { employerId },
    throwOnError: false,
  });

  useEffect(() => {
    if (data) {
      notifications
        .filter(notification => isReportPending(notification.status))
        .forEach(notification => {
          const reportToUpdate = data.employer.reports
            .filter(report => isReportComplete(report.status))
            .find(report => report.id === notification.reportId);
          if (reportToUpdate) {
            updateNotificationStatus(reportToUpdate.id, reportToUpdate.status, reportToUpdate.filename);
          }
        });
    }
  }, [data]);

  useEffect(() => {
    if (notifications.filter(notification => isReportPending(notification.status)).length > 0) {
      startPolling(5000);
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [notifications]);
};
